import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOpportunity,
  deleteOpportunityMedia,
  listOpportunities,
  updateOpportunity,
  uploadOpportunityMedia,
  type OpportunityInput,
} from "./api";

const KEY = ["opportunities"];

export const useOpportunities = () =>
  useQuery({
    queryKey: KEY,
    queryFn: listOpportunities,
    // poll while a video is still transcoding
    refetchInterval: (q) =>
      q.state.data?.some((o) =>
        o.media.some((m) => m.status === "pending" || m.status === "processing"),
      )
        ? 3000
        : false,
  });

export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OpportunityInput) => createOpportunity(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<OpportunityInput> }) =>
      updateOpportunity(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file, caption }: { id: string; file: File; caption?: string }) =>
      uploadOpportunityMedia(id, file, caption),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mediaId }: { id: string; mediaId: string }) =>
      deleteOpportunityMedia(id, mediaId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
